/**
 * Unit conversion helpers for the metric (cm/kg) <-> imperial (in/lb) display feature.
 *
 * The API is always metric: kg for weight, cm for dimensions. Imperial is purely a
 * presentation/entry concern. These pure functions are the single home for all
 * conversion + rounding logic so the behaviour is exhaustively testable. Ported from
 * ui-checkout's src/lib/units.ts (weight/dimension portions only).
 */

/** Kilograms in one pound (exact international definition). */
export const KG_PER_LB = 0.45359237;

/** Centimetres in one inch (exact). */
export const CM_PER_IN = 2.54;

// --- Exact converters (no rounding) ---

export function lbToKg(lb: number): number {
  return lb * KG_PER_LB;
}

export function kgToLb(kg: number): number {
  return kg / KG_PER_LB;
}

export function inToCm(inches: number): number {
  return inches * CM_PER_IN;
}

export function cmToIn(cm: number): number {
  return cm / CM_PER_IN;
}

// --- Display rounding for parcel values shown without a local-units entry ---

/** Weight display: nearest 0.1 lb. */
export function kgToLbDisplay(kg: number): number {
  return Math.round(kgToLb(kg) * 10) / 10;
}

/** Dimension display: nearest whole inch. */
export function cmToInDisplay(cm: number): number {
  return Math.round(cmToIn(cm));
}

// --- Limit rounding: round DOWN so a displayed maximum never overstates the true metric limit ---

/** Max dimension display: floor to whole inches. */
export function cmToInLimit(cm: number): number {
  return Math.floor(cmToIn(cm));
}

/** Max weight display: floor to 0.1 lb. */
export function kgToLbLimit(kg: number): number {
  return Math.floor(kgToLb(kg) * 10) / 10;
}

// --- Minimum-limit rounding: round UP so a converted minimum never under-states the metric floor ---

/** Min dimension: ceil to whole inches. */
export function cmToInMinLimit(cm: number): number {
  return Math.ceil(cmToIn(cm));
}

/** Min weight: ceil to 0.1 lb. */
export function kgToLbMinLimit(kg: number): number {
  return Math.ceil(kgToLb(kg) * 10) / 10;
}

// --- Entry conversion to the metric source of truth ---
// Rounded so that a metric user viewing an imperial-entered parcel never sees noise like "30.48cm".

/** Convert entered pounds to the stored kg value (2dp). */
export function toMetricWeightKg(lb: number): number {
  return Math.round(lbToKg(lb) * 100) / 100;
}

/** Convert entered inches to the stored cm value (whole cm, matching metric-entered parcels). */
export function toMetricDimensionCm(inches: number): number {
  return Math.round(inToCm(inches));
}

// --- Local-units entry + display resolution ---

export type UnitSystem = 'metric' | 'imperial';

export type DimensionAxis = 'length' | 'width' | 'height';

/** Stored on an item's Meta: exactly what the user typed, plus which unit system they used. */
export interface LocalUnitsEntry {
  unitSystem?: UnitSystem;
  originalWeightLb?: number;
  originalLengthIn?: number;
  originalWidthIn?: number;
  originalHeightIn?: number;
}

export interface UnitValue {
  value: number;
  unit: 'kg' | 'lb' | 'cm' | 'in';
}

const isImperialEntry = (localUnitsEntry?: LocalUnitsEntry): boolean => localUnitsEntry?.unitSystem === 'imperial';

/**
 * Resolve a parcel weight for display. In imperial we show the exact as-entered value when the
 * parcel was entered in imperial; otherwise we convert from the metric source of truth and round.
 */
export function resolveDisplayWeight(kg: number, unit: UnitSystem, localUnitsEntry?: LocalUnitsEntry): UnitValue {
  if (unit === 'metric') {
    return { value: kg, unit: 'kg' };
  }
  if (isImperialEntry(localUnitsEntry) && typeof localUnitsEntry?.originalWeightLb === 'number') {
    return { value: localUnitsEntry.originalWeightLb, unit: 'lb' };
  }
  return { value: kgToLbDisplay(kg), unit: 'lb' };
}

const localUnitsEntryDimensionKey: Record<DimensionAxis, keyof LocalUnitsEntry> = {
  length: 'originalLengthIn',
  width: 'originalWidthIn',
  height: 'originalHeightIn'
};

/** Resolve a single parcel dimension for display, per axis (the entry is per-axis). */
export function resolveDisplayDimension(
  cm: number,
  unit: UnitSystem,
  localUnitsEntry: LocalUnitsEntry | undefined,
  axis: DimensionAxis
): UnitValue {
  if (unit === 'metric') {
    return { value: cm, unit: 'cm' };
  }
  const original = localUnitsEntry?.[localUnitsEntryDimensionKey[axis]];
  if (isImperialEntry(localUnitsEntry) && typeof original === 'number') {
    return { value: original, unit: 'in' };
  }
  return { value: cmToInDisplay(cm), unit: 'in' };
}

/** Resolve a service/package weight limit for display. Always converts + rounds down, never the entry. */
export function resolveLimitWeight(kg: number, unit: UnitSystem): UnitValue {
  return unit === 'metric' ? { value: kg, unit: 'kg' } : { value: kgToLbLimit(kg), unit: 'lb' };
}

/** Resolve a service/package dimension limit for display. Always converts + rounds down, never the entry. */
export function resolveLimitDimension(cm: number, unit: UnitSystem): UnitValue {
  return unit === 'metric' ? { value: cm, unit: 'cm' } : { value: cmToInLimit(cm), unit: 'in' };
}

// --- Display string formatters ---
// House style: metric is compact with no space ("2kg", "30 x 20 x 10cm"); imperial uses a leading
// space ("5 lb", "12 in").

interface FormatOptions {
  localUnitsEntry?: LocalUnitsEntry;
  /** Service/package maximum: convert + round down (never use the local-units entry). */
  limit?: boolean;
}

export function formatWeightString(kg: number, unit: UnitSystem, options: FormatOptions = {}): string {
  const { value, unit: token } = options.limit
    ? resolveLimitWeight(kg, unit)
    : resolveDisplayWeight(kg, unit, options.localUnitsEntry);
  return token === 'kg' ? `${value}kg` : `${value} ${token}`;
}

export function formatDimensionsString(
  dims: { length: number; width: number; height: number },
  unit: UnitSystem,
  options: FormatOptions = {}
): string {
  const axes: DimensionAxis[] = ['length', 'width', 'height'];
  const resolved = axes.map((axis) =>
    options.limit
      ? resolveLimitDimension(dims[axis], unit)
      : resolveDisplayDimension(dims[axis], unit, options.localUnitsEntry, axis)
  );
  const token = resolved[0].unit;
  const values = resolved.map((r) => r.value).join(' x ');
  return token === 'cm' ? `${values}cm` : `${values} ${token}`;
}

// --- Shared parcel entry/display helpers (used by the unit-aware input component) ---

export type ParcelUnitField = 'weight' | DimensionAxis;

/**
 * Convert a raw field entry (in the active unit) to the metric source-of-truth value plus the
 * updated local-units entry. A metric entry clears the entire entry so no stale as-entered
 * originals can later resurface; an imperial entry records the exact typed value for that field
 * while preserving the other axes' originals.
 */
export function parcelEntryToMetric(
  field: ParcelUnitField,
  raw: string,
  unit: UnitSystem,
  localUnitsEntry: LocalUnitsEntry | undefined
): { metricValue: number | undefined; localUnitsEntry: LocalUnitsEntry } {
  const isWeight = field === 'weight';
  // Weight and metric dimensions preserve decimals (matching the prior Number(...) entry behaviour);
  // imperial dimensions are whole-inch entry only, matching the field's step={1}/min={1}.
  const parsed = raw === '' ? undefined : isWeight || unit !== 'imperial' ? parseFloat(raw) : parseInt(raw, 10);
  const entered = parsed != null && !Number.isNaN(parsed) ? parsed : undefined;

  if (unit !== 'imperial') {
    return { metricValue: entered, localUnitsEntry: { unitSystem: 'metric' } };
  }

  if (field === 'weight') {
    return {
      metricValue: entered == null ? undefined : toMetricWeightKg(entered),
      localUnitsEntry: { ...localUnitsEntry, unitSystem: 'imperial', originalWeightLb: entered ?? undefined }
    };
  }
  return {
    metricValue: entered == null ? undefined : toMetricDimensionCm(entered),
    localUnitsEntry: {
      ...localUnitsEntry,
      unitSystem: 'imperial',
      [localUnitsEntryDimensionKey[field]]: entered ?? undefined
    }
  };
}

/** Resolve a parcel field's display value in the active unit (weight or a given dimension axis). */
export function resolveParcelDisplay(
  field: ParcelUnitField,
  metricValue: number,
  unit: UnitSystem,
  localUnitsEntry: LocalUnitsEntry | undefined
): UnitValue {
  return field === 'weight'
    ? resolveDisplayWeight(metricValue, unit, localUnitsEntry)
    : resolveDisplayDimension(metricValue, unit, localUnitsEntry, field);
}

/**
 * Clear the as-entered originals for the given fields (e.g. after a metric-sourced preset/fallback
 * selection sets those fields directly). Other fields' originals are left untouched, so display for
 * an axis this action didn't touch still trusts its existing entry.
 */
export function clearLocalUnitsEntryFields(
  localUnitsEntry: LocalUnitsEntry | undefined,
  fields: ParcelUnitField[]
): LocalUnitsEntry {
  const updated: LocalUnitsEntry = { ...localUnitsEntry };
  fields.forEach((field) => {
    if (field === 'weight') {
      delete updated.originalWeightLb;
    } else {
      delete updated[localUnitsEntryDimensionKey[field]];
    }
  });
  return updated;
}

/**
 * Update the local-units entry after a preset/package selection sets metric values directly (not
 * typed by the user). In metric, this just clears those fields' originals (as before). In imperial,
 * it records the rounded value the user was actually shown in the picker for each provided field -
 * matching resolveParcelDisplay's own fallback conversion - so the field reads as an as-entered
 * imperial value rather than being silently re-derived from metric on every future render.
 */
export function applyPresetToLocalUnitsEntry(
  metricValues: Partial<Record<ParcelUnitField, number>>,
  unit: UnitSystem,
  localUnitsEntry: LocalUnitsEntry | undefined,
  providedFields: ParcelUnitField[]
): LocalUnitsEntry {
  if (unit !== 'imperial') {
    return clearLocalUnitsEntryFields(localUnitsEntry, providedFields);
  }

  const updated: LocalUnitsEntry = { ...localUnitsEntry, unitSystem: 'imperial' };
  providedFields.forEach((field) => {
    const metricValue = metricValues[field];
    if (metricValue == null) {
      return;
    }
    const displayed = resolveParcelDisplay(field, metricValue, unit, undefined).value;
    if (field === 'weight') {
      updated.originalWeightLb = displayed;
    } else if (field === 'length') {
      updated.originalLengthIn = displayed;
    } else if (field === 'width') {
      updated.originalWidthIn = displayed;
    } else {
      updated.originalHeightIn = displayed;
    }
  });
  return updated;
}

/**
 * True when the entry actually carries as-entered data - used to avoid persisting a no-op empty
 * entry. `unitSystem` alone (e.g. `{ unitSystem: 'metric' }` left behind after clearing every axis)
 * does NOT count: it conveys nothing display-wise without at least one original value, so treating
 * it as "has values" would stamp a permanent blank record for a plain metric parcel.
 */
export function localUnitsEntryHasValues(localUnitsEntry?: LocalUnitsEntry): boolean {
  return (
    localUnitsEntry != null &&
    (localUnitsEntry.originalWeightLb != null ||
      localUnitsEntry.originalLengthIn != null ||
      localUnitsEntry.originalWidthIn != null ||
      localUnitsEntry.originalHeightIn != null)
  );
}
